import React, { useEffect } from 'react';
import { 
  Bold, 
  Italic, 
  Strikethrough, 
  Superscript, 
  Link, 
  List, 
  ListOrdered, 
  Code, 
  Quote, 
  Table,
  AlignLeft,
  Type
} from 'lucide-react';

const FormattingToolbar = ({ onFormat, textRef }) => {
  const insertFormatting = (before, after = '', placeholder = '') => {
    if (!textRef.current) return;
    
    const textarea = textRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const textToInsert = selectedText || placeholder;
    
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);
    
    const newText = beforeText + before + textToInsert + after + afterText;
    const newCursorPos = start + before.length + textToInsert.length;
    
    onFormat(newText);
    
    // Set cursor position after formatting
    setTimeout(() => {
      textarea.focus();
      if (selectedText) {
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      } else {
        textarea.setSelectionRange(start + before.length, start + before.length + textToInsert.length);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const textarea = e.target;
      const start = textarea.selectionStart;
      const text = textarea.value;
      
      // Find the start of the current line
      const lineStart = text.lastIndexOf('\n', start - 1) + 1;
      const currentLine = text.substring(lineStart, start);
      
      // Check for bullet list pattern
      const bulletMatch = currentLine.match(/^(\s*)-\s*(.*)$/);
      if (bulletMatch) {
        e.preventDefault();
        const indent = bulletMatch[1];
        const content = bulletMatch[2];
        
        // If the current line is empty (just "- "), exit the list
        if (content.trim() === '') {
          const beforeText = text.substring(0, lineStart);
          const afterText = text.substring(start);
          const newText = beforeText + afterText;
          const newCursorPos = lineStart;
          
          onFormat(newText);
          setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newCursorPos, newCursorPos);
          }, 0);
          return;
        }
        
        // Continue the list
        const newBullet = `\n${indent}- `;
        const beforeText = text.substring(0, start);
        const afterText = text.substring(start);
        const newText = beforeText + newBullet + afterText;
        const newCursorPos = start + newBullet.length;
        
        onFormat(newText);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        return;
      }
      
      // Check for numbered list pattern
      const numberedMatch = currentLine.match(/^(\s*)(\d+)\.\s*(.*)$/);
      if (numberedMatch) {
        e.preventDefault();
        const indent = numberedMatch[1];
        const currentNum = parseInt(numberedMatch[2]);
        const content = numberedMatch[3];
        
        // If the current line is empty (just "1. "), exit the list
        if (content.trim() === '') {
          const beforeText = text.substring(0, lineStart);
          const afterText = text.substring(start);
          const newText = beforeText + afterText;
          const newCursorPos = lineStart;
          
          onFormat(newText);
          setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newCursorPos, newCursorPos);
          }, 0);
          return;
        }
        
        // Continue the list with incremented number
        const nextNum = currentNum + 1;
        const newNumbered = `\n${indent}${nextNum}. `;
        const beforeText = text.substring(0, start);
        const afterText = text.substring(start);
        const newText = beforeText + newNumbered + afterText;
        const newCursorPos = start + newNumbered.length;
        
        onFormat(newText);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        return;
      }
    }
  };

  const insertBulletList = () => {
    if (!textRef.current) return;
    
    const textarea = textRef.current;
    const start = textarea.selectionStart;
    const text = textarea.value;
    
    // Check if we're already in a list
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const currentLine = text.substring(lineStart, start);
    
    if (currentLine.trim() === '') {
      // Empty line, start a new bullet list
      insertFormatting('- ', '', 'list item');
    } else {
      // Add bullet to current line
      const beforeText = text.substring(0, lineStart);
      const afterText = text.substring(start);
      const newText = beforeText + '- ' + currentLine + afterText;
      const newCursorPos = start + 2;
      
      onFormat(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const insertNumberedList = () => {
    if (!textRef.current) return;
    
    const textarea = textRef.current;
    const start = textarea.selectionStart;
    const text = textarea.value;
    
    // Check if we're already in a list
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const currentLine = text.substring(lineStart, start);
    
    if (currentLine.trim() === '') {
      // Empty line, start a new numbered list
      insertFormatting('1. ', '', 'list item');
    } else {
      // Add number to current line
      const beforeText = text.substring(0, lineStart);
      const afterText = text.substring(start);
      const newText = beforeText + '1. ' + currentLine + afterText;
      const newCursorPos = start + 3;
      
      onFormat(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  useEffect(() => {
    const textarea = textRef?.current;
    if (textarea) {
      textarea.addEventListener('keydown', handleKeyDown);
      
      return () => {
        textarea.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, []);

  const formatButtons = [
    {
      icon: Bold,
      title: 'Bold',
      onClick: () => insertFormatting('**', '**', 'bold text'),
    },
    {
      icon: Italic,
      title: 'Italic',
      onClick: () => insertFormatting('*', '*', 'italic text'),
    },
    {
      icon: Strikethrough,
      title: 'Strikethrough',
      onClick: () => insertFormatting('~~', '~~', 'strikethrough text'),
    },
    {
      icon: Superscript,
      title: 'Superscript',
      onClick: () => insertFormatting('^(', ')', 'superscript'),
    },
    {
      icon: Code,
      title: 'Inline Code',
      onClick: () => insertFormatting('`', '`', 'code'),
    },
    {
      icon: Link,
      title: 'Link',
      onClick: () => insertFormatting('[', '](url)', 'link text'),
    },
    {
      icon: List,
      title: 'Bulleted List',
      onClick: () => insertBulletList(),
    },
    {
      icon: ListOrdered,
      title: 'Numbered List',
      onClick: () => insertNumberedList(),
    },
    {
      icon: Quote,
      title: 'Quote',
      onClick: () => insertFormatting('\n> ', '', 'quoted text'),
    },
    {
      icon: Table,
      title: 'Table',
      onClick: () => insertFormatting('\n| Column 1 | Column 2 |\n|----------|----------|\n| ', ' | Cell 2 |\n', 'Cell 1'),
    },
    {
      icon: Type,
      title: 'Heading',
      onClick: () => insertFormatting('\n## ', '', 'heading'),
    },
    {
      icon: AlignLeft,
      title: 'Code Block',
      onClick: () => insertFormatting('\n```\n', '\n```\n', 'code block'),
    },
  ];

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-t-md bg-gray-50 dark:bg-gray-800 p-2 flex flex-wrap gap-1">
      {formatButtons.map((button, index) => {
        const Icon = button.icon;
        return (
          <button
            key={index}
            type="button"
            onClick={button.onClick}
            title={button.title}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
          >
            <Icon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>
        );
      })}
    </div>
  );
};

export default FormattingToolbar;